import { useState } from "react";
import { Grape, User, Lock, Eye, EyeOff, LogIn, TriangleAlert, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const BG = "https://images.unsplash.com/photo-1504279577054-acfeccf8fc52?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.detail));
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-brand" data-testid="login-screen">
      {/* Left: brand / imagery */}
      <div className="hidden lg:block relative flex-1">
        <img src={BG} alt="Viñedo" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
              <Grape size={24} />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl tracking-tight">Treeplet</div>
              <div className="text-xs text-white/70 tracking-widest uppercase">Centro de Operaciones</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading font-extrabold text-4xl leading-tight">
              Protege tu viñedo,
              <br />un día por delante.
            </h1>
            <p className="text-white/75 mt-4 text-base leading-relaxed">
              Inteligencia agrícola para prevenir el mildiu y las heladas. Recomendaciones claras, cuando importan.
            </p>
          </div>
          <div className="text-xs text-white/50">© 2026 Treeplet · Acceso exclusivo para clientes</div>
        </div>
      </div>

      {/* Right: login card */}
      <div className="w-full lg:w-[460px] shrink-0 bg-page flex items-center justify-center p-8">
        <div className="w-full max-w-[360px]">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-lg bg-brand text-white flex items-center justify-center">
              <Grape size={24} />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl tracking-tight text-ink">Treeplet</div>
              <div className="text-xs text-ink-tertiary tracking-widest uppercase">Centro de Operaciones</div>
            </div>
          </div>

          <div className="w-16 h-16 rounded-full bg-brand text-white flex items-center justify-center mx-auto mb-4">
            <User size={30} />
          </div>
          <h2 className="font-heading font-extrabold text-2xl text-ink text-center">Iniciar sesión</h2>
          <p className="text-sm text-ink-secondary text-center mt-1 mb-7">Introduce tus credenciales para continuar</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-ink-secondary mb-1.5 block">Correo electrónico</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  autoFocus
                  required
                  placeholder="nombre@viñedo.com"
                  className="w-full h-12 pl-10 pr-3 rounded-lg bg-surface border border-line text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-ink-secondary mb-1.5 block">Contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  required
                  placeholder="••••••••"
                  className="w-full h-12 pl-10 pr-11 rounded-lg bg-surface border border-line text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink"
                  tabIndex={-1}
                  aria-label="Mostrar contraseña"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-critical bg-critical-bg border border-critical-border rounded-lg px-3 py-2.5" data-testid="login-error">
                <TriangleAlert size={15} className="shrink-0" /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 rounded-lg bg-brand hover:bg-brand-hover text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <LogIn size={17} />}
              {loading ? "Comprobando…" : "Iniciar sesión"}
            </button>
          </form>

          <div className="mt-6 text-[11px] text-ink-tertiary text-center leading-relaxed border-t border-line pt-4">
            Acceso demo · <span className="font-semibold text-ink-secondary">celler@treeplet.com</span> / <span className="font-semibold text-ink-secondary">verema2025</span>
          </div>
        </div>
      </div>
    </div>
  );
}
